import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Badge,
} from '@chakra-ui/react';
import { FaCalendarAlt, FaClock, FaUser, FaVideo } from 'react-icons/fa';
import { format, parseISO, isAfter } from 'date-fns';
import type { PortalCall } from '../types';

const PROPERTY_PRIMARY = '#0D7F6E';

interface ScheduledCallCardProps {
  call: PortalCall;
}

export const ScheduledCallCard: React.FC<ScheduledCallCardProps> = ({ call }) => {
  const callDate = parseISO(call.scheduledAt);
  const isUpcoming = isAfter(callDate, new Date());

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor={isUpcoming ? 'teal.200' : 'gray.200'}
      borderRadius="xl"
      p={4}
      boxShadow="sm"
      position="relative"
      overflow="hidden"
      _before={isUpcoming ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '4px',
        height: '100%',
        bg: PROPERTY_PRIMARY,
        borderRadius: '4px 0 0 4px',
      } : undefined}
    >
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between">
          <Text fontWeight="semibold" fontSize="sm" color="gray.800">
            {call.title}
          </Text>
          {isUpcoming && (
            <Badge colorScheme="teal" borderRadius="full" fontSize="xs">
              Upcoming
            </Badge>
          )}
        </HStack>

        <VStack align="stretch" spacing={1}>
          <HStack spacing={2} color="gray.600">
            <Icon as={FaCalendarAlt} boxSize={3.5} />
            <Text fontSize="xs">{format(callDate, 'EEEE d MMMM yyyy')}</Text>
          </HStack>
          <HStack spacing={2} color="gray.600">
            <Icon as={FaClock} boxSize={3.5} />
            <Text fontSize="xs">
              {format(callDate, 'h:mm a')} · {call.durationMinutes} min
            </Text>
          </HStack>
          <HStack spacing={2} color="gray.600">
            <Icon as={FaUser} boxSize={3.5} />
            <Text fontSize="xs">{call.hostName} · {call.hostRole}</Text>
          </HStack>
        </VStack>

        {call.meetingUrl && isUpcoming && (
          <Button
            as="a"
            href={call.meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            bg={PROPERTY_PRIMARY}
            color="white"
            _hover={{ bg: '#0a6b5d' }}
            leftIcon={<FaVideo />}
            borderRadius="lg"
          >
            Join Call
          </Button>
        )}

        {!call.meetingUrl && isUpcoming && (
          <Text fontSize="xs" color="gray.400" fontStyle="italic">
            Meeting link will be shared before the call
          </Text>
        )}
      </VStack>
    </Box>
  );
};
